import React, { useRef, useState } from 'react';
import { Form, Input, Select, Button, Spin, App } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { useLazyLookupClienteByRfcQuery } from '../../services/autofacturaApi';
import type { CatalogoRegimenFiscal, CatalogoUsoCFDI } from '../../types/facturacion';
import type { AutofacturaGenerarRequest } from '../../types/autofactura';

const { Option } = Select;

interface FiscalDataStepProps {
  code: string;
  orderNumber: number;
  total: number;
  regimenesFiscales?: CatalogoRegimenFiscal[];
  usosCFDI?: CatalogoUsoCFDI[];
  catalogsLoading: boolean;
  onSubmit: (data: AutofacturaGenerarRequest) => void;
  submitting: boolean;
}

const normalizeSATCode = (code: string): string => {
  if (code.length === 1 && /^\d$/.test(code)) {
    return '0' + code;
  }
  return code;
};

const findCatalogMatch = (
  catalogo: Array<{ clave: string }> | undefined,
  value: string
): string | null => {
  if (!catalogo) return null;
  const exactMatch = catalogo.find(item => item.clave === value);
  if (exactMatch) return exactMatch.clave;
  const withoutLeadingZero = value.replace(/^0/, '');
  const matchWithoutZero = catalogo.find(item => item.clave === withoutLeadingZero);
  if (matchWithoutZero) return matchWithoutZero.clave;
  if (value.length === 1 && /^\d$/.test(value)) {
    const withLeadingZero = '0' + value;
    const matchWithZero = catalogo.find(item => item.clave === withLeadingZero);
    if (matchWithZero) return matchWithZero.clave;
  }
  return null;
};

const FiscalDataStep: React.FC<FiscalDataStepProps> = ({
  code,
  orderNumber,
  total,
  regimenesFiscales,
  usosCFDI,
  catalogsLoading,
  onSubmit,
  submitting,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [lookupCliente, { isFetching: lookingUp }] = useLazyLookupClienteByRfcQuery();
  const [rfcLookedUp, setRfcLookedUp] = useState(false);
  const lastLookedUpRfc = useRef<string>('');

  const rfcPattern = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/i;

  const doRfcLookup = async (rfc: string) => {
    const cleanRfc = rfc.trim().toUpperCase();
    if (!rfcPattern.test(cleanRfc) || cleanRfc === lastLookedUpRfc.current) return;
    lastLookedUpRfc.current = cleanRfc;

    try {
      const result = await lookupCliente({ code, rfc: cleanRfc }).unwrap();
      if (result) {
        const regimenMatch = findCatalogMatch(regimenesFiscales, result.regimenFiscal);
        const usoMatch = findCatalogMatch(usosCFDI, result.usoCFDI);

        form.setFieldsValue({
          razonSocial: result.razonSocial,
          regimenFiscal: regimenMatch || result.regimenFiscal,
          usoCFDI: usoMatch || result.usoCFDI,
          codigoPostal: result.codigoPostal || '',
          email: result.email || '',
        });
        setRfcLookedUp(true);
        message.success('Datos fiscales encontrados');
      }
    } catch {
      setRfcLookedUp(false);
    }
  };

  const handleRfcChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRfcLookedUp(false);
    if (value.length >= 12) {
      doRfcLookup(value);
    }
  };

  const handleFinish = (values: any) => {
    onSubmit({
      orderNumber,
      total,
      rfc: values.rfc.trim().toUpperCase(),
      razonSocial: values.razonSocial.trim(),
      regimenFiscal: normalizeSATCode(values.regimenFiscal),
      usoCFDI: normalizeSATCode(values.usoCFDI),
      codigoPostal: values.codigoPostal.trim(),
      email: values.email?.trim() || undefined,
    });
  };

  if (catalogsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Cargando catálogos fiscales...</p>
      </div>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      size="large"
    >
      <Form.Item
        label="RFC"
        name="rfc"
        rules={[
          { required: true, message: 'El RFC es requerido' },
          {
            pattern: /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/i,
            message: 'RFC inválido',
          },
        ]}
      >
        <Input
          placeholder="Ej: XAXX010101000"
          maxLength={13}
          style={{ textTransform: 'uppercase' }}
          onChange={handleRfcChange}
          suffix={
            lookingUp ? <Spin size="small" /> :
            rfcLookedUp ? <CheckCircleFilled style={{ color: '#52c41a' }} /> :
            null
          }
        />
      </Form.Item>

      <Form.Item
        label="Razón Social"
        name="razonSocial"
        rules={[{ required: true, message: 'La razón social es requerida' }]}
      >
        <Input placeholder="Nombre o razón social" />
      </Form.Item>

      <Form.Item
        label="Régimen Fiscal"
        name="regimenFiscal"
        rules={[{ required: true, message: 'El régimen fiscal es requerido' }]}
      >
        <Select
          placeholder="Seleccione régimen fiscal"
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.children as unknown as string)
              ?.toLowerCase()
              .includes(input.toLowerCase()) ?? false
          }
        >
          {regimenesFiscales?.map((reg) => (
            <Option key={reg.clave} value={reg.clave}>
              {reg.clave} - {reg.descripcion}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Uso del CFDI"
        name="usoCFDI"
        rules={[{ required: true, message: 'El uso del CFDI es requerido' }]}
      >
        <Select
          placeholder="Seleccione uso del CFDI"
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.children as unknown as string)
              ?.toLowerCase()
              .includes(input.toLowerCase()) ?? false
          }
        >
          {usosCFDI?.map((uso) => (
            <Option key={uso.clave} value={uso.clave}>
              {uso.clave} - {uso.descripcion}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Código Postal"
        name="codigoPostal"
        rules={[
          { required: true, message: 'El código postal es requerido' },
          { pattern: /^\d{5}$/, message: 'Código postal inválido (5 dígitos)' },
        ]}
      >
        <Input placeholder="Ej: 06600" maxLength={5} />
      </Form.Item>

      <Form.Item
        label="Email (opcional)"
        name="email"
        rules={[
          { type: 'email', message: 'Email inválido' },
        ]}
      >
        <Input placeholder="Para recibir tu factura por correo" />
      </Form.Item>

      <Form.Item style={{ marginBottom: 0 }}>
        <Button
          type="primary"
          htmlType="submit"
          block
          size="large"
          loading={submitting}
          style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
        >
          Siguiente
        </Button>
      </Form.Item>
    </Form>
  );
};

export default FiscalDataStep;
